<template>
  <div class="landing-wrapper">
    <v-container class="py-8 py-md-12" fluid>
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-alert v-if="error" type="error" class="mb-4" closable @click:close="error = ''">
            {{ error }}
          </v-alert>

          <div v-if="loading" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary" size="48" />
          </div>

          <div v-else class="obra-form-panel">
            <div class="obra-form-header">
              <v-icon size="22" color="#ffffff">mdi-lifebuoy</v-icon>
              <span>{{ editing ? 'EDITAR SOPORTE' : 'NUEVO SOPORTE' }}</span>
            </div>

            <div class="obra-form-body">
              <v-row>
                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="form.name"
                    label="Nombre del soporte"
                    placeholder="Ej: Coordinador, Sonido, Iluminación"
                    variant="outlined"
                    :error-messages="fieldError('name')"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-switch v-model="form.active" label="Activo" color="primary" />
                </v-col>
                <!-- "Orden" ya no se edita desde acá; el campo sigue en el modelo
                     y su valor se conserva tal cual al guardar. -->
                <v-col cols="12">
                  <v-textarea
                    v-model="form.description"
                    label="Descripción"
                    rows="2"
                    variant="outlined"
                  />
                </v-col>
              </v-row>

              <div class="sf-contacts-head">
                <h3 class="sf-section">CONTACTOS DE ESTE SOPORTE</h3>
                <button type="button" class="sf-add-btn" @click="addContact">
                  <v-icon size="16">mdi-plus</v-icon>
                  AGREGAR CONTACTO
                </button>
              </div>

              <p v-if="form.contacts.length === 0" class="sf-empty">
                Todavía no hay contactos. Agrega al menos uno.
              </p>

              <div
                v-for="(c, i) in form.contacts"
                :key="i"
                class="sf-contact"
              >
                <div class="sf-contact-head">
                  <span class="sf-contact-num">CONTACTO {{ i + 1 }}</span>
                  <button type="button" class="sf-remove-btn" @click="removeContact(i)">
                    <v-icon size="15">mdi-trash-can-outline</v-icon>
                    QUITAR
                  </button>
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="c.name" label="Nombre" variant="outlined" />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field v-model="c.phone" label="Teléfono" variant="outlined" />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field v-model="c.email" label="Correo" type="email" variant="outlined" />
                  </v-col>
                  <v-col cols="12" md="9">
                    <v-text-field v-model="c.description" label="Descripción" variant="outlined" />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-switch v-model="c.active" label="Activo" color="primary" />
                  </v-col>
                </v-row>
              </div>

              <div class="obra-form-actions">
                <button
                  v-if="editing"
                  type="button"
                  class="sf-delete-btn"
                  :disabled="saving"
                  @click="removeSupport"
                >
                  ELIMINAR SOPORTE
                </button>
                <button type="button" class="sf-ghost-btn" @click="goBack">
                  CANCELAR Y VOLVER
                </button>
                <button
                  type="button"
                  class="sf-primary-btn"
                  :disabled="saving"
                  @click="save"
                >
                  {{ saving ? 'GUARDANDO…' : 'GUARDAR' }}
                </button>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <teatro-footer />
  </div>
</template>

<script>
import {
  fetchSupport,
  createSupport,
  updateSupport,
  deleteSupport,
} from "@/services/teatroApi";

function emptyContact() {
  return { name: "", description: "", phone: "", email: "", active: true };
}

export default {
  name: "SupportForm",
  props: {
    supportId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      form: {
        name: "",
        description: "",
        active: true,
        order: 0,
        contacts: [],
      },
      fieldErrors: {},
      loading: true,
      saving: false,
      error: "",
    };
  },
  computed: {
    editing() {
      return this.supportId !== null && this.supportId !== "";
    },
  },
  async created() {
    await this.loadAuth();
    await this.load();
  },
  methods: {
    async loadAuth() {
      try {
        await this.$store.dispatch("fetchIsAuthenticated");
      } catch (e) {
        console.error(e);
      }
    },
    async load() {
      this.loading = true;
      try {
        if (this.editing) {
          const s = await fetchSupport(this.supportId);
          this.form = {
            name: s.name || "",
            description: s.description || "",
            active: s.active !== false,
            order: s.order || 0,
            contacts: (s.contacts || []).map((c) => ({
              name: c.name || "",
              description: c.description || "",
              phone: c.phone || "",
              email: c.email || "",
              active: c.active !== false,
            })),
          };
        } else {
          this.form.contacts = [emptyContact()];
        }
      } catch (e) {
        this.error = "No se pudo cargar el soporte.";
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    fieldError(name) {
      return this.fieldErrors[name] || [];
    },
    addContact() {
      this.form.contacts.push(emptyContact());
    },
    removeContact(index) {
      this.form.contacts.splice(index, 1);
    },
    async save() {
      this.saving = true;
      this.error = "";
      this.fieldErrors = {};
      try {
        const payload = {
          ...this.form,
          // Se descartan las filas en blanco que quedaron sin llenar.
          contacts: this.form.contacts
            .filter((c) => (c.name || "").trim())
            .map((c, i) => ({ ...c, order: i })),
        };
        if (this.editing) {
          await updateSupport(this.supportId, payload);
        } else {
          await createSupport(payload);
        }
        window.location.href = "/soportes/";
      } catch (e) {
        const data = e.response?.data;
        if (data && typeof data === "object" && !Array.isArray(data)) {
          this.fieldErrors = data;
          this.error = data.detail || "Revisa los campos marcados.";
        } else {
          this.error = "No se pudo guardar el soporte.";
        }
        console.error(e);
        this.saving = false;
      }
    },
    async removeSupport() {
      const ok = window.confirm(
        "¿Eliminar este soporte? Se borran también todos sus contactos."
      );
      if (!ok) return;
      this.saving = true;
      try {
        await deleteSupport(this.supportId);
        window.location.href = "/soportes/";
      } catch (e) {
        this.error = "No se pudo eliminar el soporte.";
        console.error(e);
        this.saving = false;
      }
    },
    goBack() {
      window.location.href = "/soportes/";
    },
  },
};
</script>

<style scoped>
/* Mismo panel de formulario que PlayForm / CompanyProfile / ClientForm. */
.obra-form-panel {
  max-width: 1080px;
  margin: 0 auto;
  background: rgba(8, 5, 5, 0.66);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}
.obra-form-header {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 66px;
  padding: 0 32px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 1.4px;
}
.obra-form-body {
  padding: 30px 34px 28px;
}
.obra-form-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 26px;
  margin-top: 24px;
}
.obra-form-panel :deep(.v-field) {
  background: #ffffff !important;
  border-radius: 8px !important;
}
.obra-form-panel :deep(.v-field__outline) {
  --v-field-border-opacity: 0;
  color: transparent !important;
}
.obra-form-panel :deep(.v-field__input),
.obra-form-panel :deep(.v-field input) {
  color: #1a1a1a !important;
}
.obra-form-panel :deep(.v-field-label) {
  color: #7a7a7a !important;
  opacity: 1;
}
.obra-form-panel :deep(.v-field-label.v-field-label--floating) {
  color: #ffffff !important;
  background: #17100f;
  padding: 1px 6px;
  border-radius: 4px;
}
.obra-form-panel :deep(.v-messages),
.obra-form-panel :deep(.v-messages__message) {
  color: rgba(255, 255, 255, 0.75) !important;
  opacity: 1;
}
.obra-form-panel :deep(.v-switch .v-label) {
  color: var(--color-theater-text) !important;
  opacity: 1;
}

/* --- Contactos ----------------------------------------------------------- */
.sf-contacts-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 26px 0 14px;
  flex-wrap: wrap;
}

.sf-section {
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 1.2px;
  margin: 0;
}

.sf-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 8px 16px;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.9px;
  cursor: pointer;
  transition: border-color 0.15s ease;
}

.sf-add-btn:hover {
  border-color: rgba(255, 255, 255, 0.5);
}

.sf-contact {
  padding: 16px 18px;
  margin-bottom: 14px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.sf-contact-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.sf-contact-num {
  color: var(--color-theater-text-muted);
  font-family: var(--font-family-heading);
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 1px;
}

.sf-remove-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  border: none;
  color: #ff8ea0;
  font-family: var(--font-family-heading);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.8px;
  cursor: pointer;
}

.sf-remove-btn:hover {
  text-decoration: underline;
}

.sf-empty {
  color: rgba(255, 255, 255, 0.6);
  font-family: var(--font-family-heading);
  font-size: 12px;
  margin: 0 0 14px;
}

.sf-ghost-btn {
  background: transparent;
  border: none;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  cursor: pointer;
  padding: 12px 8px;
  transition: opacity 0.2s ease;
}

.sf-ghost-btn:hover {
  opacity: 0.72;
}

.sf-delete-btn {
  margin-right: auto;
  background: transparent;
  border: 1px solid rgba(200, 16, 46, 0.6);
  border-radius: 8px;
  color: #ff8ea0;
  font-family: var(--font-family-heading);
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.9px;
  cursor: pointer;
  padding: 10px 18px;
  transition: background 0.2s ease;
}

.sf-delete-btn:hover:not(:disabled) {
  background: rgba(200, 16, 46, 0.15);
}

.sf-primary-btn {
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 30px;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.sf-primary-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(200, 16, 46, 0.4);
}

.sf-primary-btn:disabled,
.sf-delete-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
</style>
